import { useEffect } from "react";
import { Button } from "react-aria-components";

import { toast } from "@/components/toast/toast-queue";
import {
  getCurrentAppVersion,
  getDismissedVersion,
  setDismissedVersion,
  updateToNewVersion,
} from "@/lib/version-manager";

let lastShownVersion: null | string = null;

async function handleUpdateClick() {
  await updateToNewVersion();
}

function showUpdateToast(newVersion: string) {
  if (lastShownVersion === newVersion) return;
  lastShownVersion = newVersion;

  toast.add(
    {
      render: () => (
        <div className="flex flex-1 items-center gap-xl justify-between pr-xl">
          <div className="flex flex-col gap-xs text-sm">
            <div className="font-semibold text-fg-primary">
              New version available
            </div>
            <div className="text-fg-secondary">
              A new version of the platform has been released.
            </div>
          </div>
          <Button
            className="whitespace-nowrap px-3 py-1.5 text-sm font-medium text-fg-primary hover:bg-bg-secondary rounded transition"
            onPress={handleUpdateClick}
          >
            Update now
          </Button>
        </div>
      ),
      timeout: 0,
      type: "warning",
    },
    {
      onClose: () => setDismissedVersion(newVersion),
      timeout: 0,
    },
  );
}

function handleSWMessage(event: MessageEvent) {
  const message = event.data;

  if (message.type === "VERSION_CHECK") {
    const runningVersion = getCurrentAppVersion();

    if (
      message.version &&
      message.version !== runningVersion &&
      message.version !== getDismissedVersion()
    ) {
      showUpdateToast(message.version);
    }
  }
}

async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register(
      "/serviceworker.js",
      { scope: "/" },
    );

    navigator.serviceWorker.addEventListener("message", handleSWMessage);

    setInterval(
      () => {
        registration.update().catch(() => {});
      },
      5 * 60 * 1000,
    );
  } catch {}
}

export function useVersionCheck() {
  useEffect(() => {
    registerServiceWorker();
  }, []);
}
